/**
 * Chat de soporte: API neutra.
 *
 * Es lo único que importa el resto de la app. El nombre del proveedor, su API y
 * su selector en el DOM viven en el adaptador de al lado y no salen de ahí, así
 * que cambiar de proveedor es escribir otro adaptador y tocar el import de abajo.
 *
 * Dos cosas se hacen aquí y no en el adaptador porque no deben depender de que el
 * proveedor de turno las ofrezca:
 *
 * - **Ocultar el widget fuera de las páginas que lo llevan** se hace con una clase
 *   en `<html>` y CSS, como ya se hace con CookieYes (`components/CookieConsent.jsx`).
 *   La clase está puesta POR DEFECTO desde `main.jsx`, antes del primer render, y
 *   se retira al montar: al revés, entrar directo por `/login` haría parpadear la
 *   burbuja antes de que React montara nada.
 * - **Saber si está abierto** se deduce del DOM (`openState.js`). Richpanel no
 *   publica ningún evento de apertura ni cierre, así que no hay nada a lo que
 *   suscribirse.
 */
import * as provider from './richpanel';
import { observeOpenState } from './openState';
import { buildSupportIdentity, buildSupportTraits } from './traits';

/** Puesta por defecto en `main.jsx`; se retira mientras haya un `<SupportChat>` montado. */
export const HIDDEN_CLASS = 'support-chat-hidden';
/** Presente mientras el panel del chat está abierto. */
export const OPEN_CLASS = 'support-chat-open';
/**
 * Presente mientras haya montado un `<SupportChat launcher={false}>`: el chat se
 * abre desde un botón nuestro (el menú del avatar en la app) y la burbuja
 * flotante sobra. Esconde SOLO la burbuja, nunca el panel.
 */
export const NO_LAUNCHER_CLASS = 'support-chat-no-launcher';

export const WIDGET_ROOT_SELECTOR = provider.WIDGET_ROOT_SELECTOR;
// El proveedor reparte sus cajas en dos grupos porque no se estilan igual: las
// `static` hay que envolverlas para poder desplazarlas y las que ya son `fixed`
// se transforman tal cual. Lo consume el CSS de elevación de `SupportChat.jsx`.
export const STATIC_ROOT_SELECTOR = provider.STATIC_ROOT_SELECTOR;
export const FIXED_ROOT_SELECTOR = provider.FIXED_ROOT_SELECTOR;

// Contador de montajes en vez de mostrar/ocultar directamente. Es barato y evita
// que un solapamiento entre el desmontaje de una página y el montaje de la
// siguiente deje el widget escondido.
let mounted = 0;
// Cuántos de los montados no quieren burbuja. Contador por lo mismo que
// `mounted`: al navegar la página entrante monta antes de que desmonte la
// saliente, y un booleano lo dejaría en el valor equivocado.
let sinBurbuja = 0;
let scriptInjected = false;
let stopObserver = null;
let lastIdentifyPayload = null;

const STYLE_ELEMENT_ID = 'support-chat-visibility';

/**
 * Compone un selector CSS repartiendo el prefijo y el sufijo por CADA selector
 * de la lista.
 *
 * Existe porque los selectores del adaptador pueden ser LISTAS, y pegarle un
 * prefijo por delante solo condiciona al primero: de `html.oculto` + `.a, .b`
 * sale `html.oculto .a, .b`, y `.b` acaba aplicandose SIEMPRE. Con una regla de
 * `display: none` eso es un widget que no se ve nunca.
 *
 * No es hipotético: es exactamente lo que dejó el chat inutilizable en
 * producción. El selector decía `.richpanel-micro, iframe[class*=...]`, el
 * segundo trozo se quedó suelto, y en cuanto Richpanel creo ese iframe —cosa
 * que solo pasa al abrir el panel por primera vez— se lo comió un
 * `display: none` incondicional. El widget desaparecía al primer click.
 *
 * Limitacion asumida: parte por comas, asi que el adaptador no puede meter comas
 * dentro de corchetes ni de `:is()`.
 */
export function scopeSelector(list, prefix = '', suffix = '') {
  return list
    .split(',')
    .map((part) => `${prefix} ${part.trim()} ${suffix}`.trim())
    .join(', ');
}

/** Lo mismo sobre TODO lo que el widget cuelga del body. */
export function widgetSelector(prefix = '', suffix = '') {
  return scopeSelector(provider.WIDGET_ROOT_SELECTOR, prefix, suffix);
}

/**
 * El CSS que acompaña al widget esté donde esté: dónde NO se ve y a qué altura
 * de la pila vive. Se exporta para poder leerlo en los tests.
 *
 * ## El tope de z-index
 *
 * El proveedor cuelga sus cajas con `z-index: 2147483647`, que es el máximo que
 * admite la especificación: por encima de absolutamente todo lo nuestro. Da
 * igual lo que la app ponga encima —los velos a pantalla completa de «conecta tu
 * ficha de Google» y de la vista previa (`z-30`), los diálogos (`z-50`), los
 * avisos (`z-100`), el spinner (`z-9999`)—: la burbuja se queda flotando sobre
 * ellos, y encima es lo único de la pantalla que en ese momento sí se puede
 * pulsar. Se ve sobre todo en el velo de Google, que es grande y sale solo.
 *
 * Se le pone un tope NUESTRO, y solo mientras el chat está CERRADO: abierto
 * vuelve a su z-index nativo, porque un panel de conversación que se abriera
 * detrás del velo sería peor que el problema que arregla.
 *
 * Los dos grupos de cajas se topan distinto, y por lo mismo que ya los separa en
 * el adaptador:
 *
 * - La caja `static` no admite `z-index` tal cual, así que se posiciona
 *   (`relative`, que en una caja de altura cero y en flujo no mueve nada) y con
 *   eso pasa a ser un contexto de apilamiento: el `2147483647` de sus hijos ya
 *   solo compite DENTRO de ella. En móvil el CSS de elevación la vuelve `fixed`
 *   con `!important` y gana esa; el `z-index` vale igual en los dos casos.
 * - La que ya es `fixed` lleva su z-index EN LÍNEA, así que hace falta
 *   `!important` para ganarle.
 */
export function buildGlobalCss() {
  const cerrado = `html:not(.${OPEN_CLASS})`;
  const z = 'var(--support-chat-z, 20)';
  return [
    `${widgetSelector(`html.${HIDDEN_CLASS}`)} { display: none !important; }`,
    `${scopeSelector(provider.STATIC_ROOT_SELECTOR, cerrado)} `
      + `{ position: relative; z-index: ${z}; }`,
    `${scopeSelector(provider.FIXED_ROOT_SELECTOR, cerrado)} `
      + `{ z-index: ${z} !important; }`,
    // Solo la burbuja: el panel tiene que poder abrirse desde nuestro botón.
    // Por `scopeSelector` y no concatenando, por lo que cuenta su comentario.
    provider.LAUNCHER_SELECTOR
      ? `${scopeSelector(provider.LAUNCHER_SELECTOR, `html.${NO_LAUNCHER_CLASS}`)} `
        + '{ display: none !important; }'
      : null,
  ].filter(Boolean).join('\n');
}

/**
 * Se inyecta en `<head>` a mano y no se renderiza desde `<SupportChat>`, porque
 * tiene que seguir ahí precisamente CUANDO el componente está desmontado — que
 * es cuando hay que esconder el widget. Un `<style>` de React se iría con él y
 * la burbuja reaparecería en `/login`.
 */
function ensureGlobalStyles() {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = buildGlobalCss();
  document.head.appendChild(style);
}

function applyOpenClass(open) {
  document.documentElement.classList.toggle(OPEN_CLASS, open);
}

function startOpenStateTracking() {
  stopObserver = observeOpenState({
    selector: provider.WIDGET_ROOT_SELECTOR,
    // De las cajas del widget, la `static` es justo la que nuestro propio CSS de
    // elevación convierte en una caja a pantalla completa: medirla daría
    // "abierto" siempre y ese CSS se desactivaría solo.
    ignoreRectSelector: provider.STATIC_ROOT_SELECTOR,
    onChange: applyOpenClass,
  });
}

function applyLauncherClass() {
  document.documentElement.classList.toggle(NO_LAUNCHER_CLASS, sinBurbuja > 0);
}

/**
 * Monta el widget. Inyecta el script en el primer montaje y nunca más.
 *
 * @param {{ launcher?: boolean, verifiedIdentity?: string | null }} [options]
 *   - `launcher: false` esconde la burbuja mientras este montaje viva.
 *   - `verifiedIdentity` es la identidad cifrada del servidor. Solo cuenta si
 *     llega ANTES de inyectar el script: Richpanel la lee al cargar.
 */
export function acquireSupportChat({ launcher = true, verifiedIdentity = null } = {}) {
  if (!launcher) {
    sinBurbuja += 1;
    applyLauncherClass();
  }
  mounted += 1;
  if (mounted > 1) return;
  if (!scriptInjected) {
    ensureGlobalStyles();
    provider.setVerifiedIdentity?.(verifiedIdentity);
    provider.injectScript();
    scriptInjected = true;
    startOpenStateTracking();
  }
  document.documentElement.classList.remove(HIDDEN_CLASS);
  provider.showWidget?.();
}

/**
 * Desmonta el widget. El script sigue cargado: solo se esconde la interfaz.
 * `launcher` tiene que ser el mismo que se pasó a `acquireSupportChat`.
 */
export function releaseSupportChat({ launcher = true } = {}) {
  if (!launcher) {
    sinBurbuja = Math.max(0, sinBurbuja - 1);
    applyLauncherClass();
  }
  mounted = Math.max(0, mounted - 1);
  if (mounted > 0) return;
  document.documentElement.classList.add(HIDDEN_CLASS);
  provider.hideWidget?.();
}

/**
 * Abre el chat. Devuelve `false` si no lo consiguió, para que quien llama pueda
 * ofrecer otra cosa en vez de dejar un botón muerto.
 *
 * Va en cascada porque no todos los proveedores dejan abrir el panel por código:
 * primero su API, después un click sintético sobre el lanzador (que solo llega si
 * el lanzador está en NUESTRO documento — sobre un `<iframe>` de otro origen el
 * evento no entra), y por último el correo de ventas.
 *
 * @param {{ mailto?: string }} [options]
 */
export function openSupportChat({ mailto } = {}) {
  if (provider.openWidget?.()) return true;

  const root = document.querySelector(provider.WIDGET_ROOT_SELECTOR);
  const launcher = root?.querySelector('button, [role="button"]') || root;
  if (launcher && !(launcher instanceof HTMLIFrameElement)) {
    launcher.dispatchEvent(new MouseEvent('click', {
      bubbles: true, cancelable: true, view: window,
    }));
    // El click puede haber funcionado o no; el observador lo dirá en el siguiente
    // frame, pero quien llama necesita una respuesta ahora. Se considera
    // conseguido solo si el panel ya estaba o pasa a estar abierto.
    if (document.documentElement.classList.contains(OPEN_CLASS)) return true;
  }

  if (mailto) {
    window.location.href = mailto;
    return true;
  }
  return false;
}

/**
 * Adjunta al panel de soporte quién es el usuario y en qué estado está su cuenta.
 *
 * El guard de repetición no es cosmético: `getGbusinessMe` devuelve un objeto
 * nuevo en cada refetch de `['gbusiness','me']`, así que sin él cada refetch
 * dispararía una identificación idéntica. Con Crisp eso era empujar a una cola en
 * memoria; con un proveedor que identifique por HTTP es una petición de red.
 */
export function identifySupportUser(user, billing) {
  const identity = buildSupportIdentity(user);
  if (!identity) return;
  const traits = buildSupportTraits(user, billing);
  const payload = JSON.stringify({ identity, traits });
  if (payload === lastIdentifyPayload) return;
  lastIdentifyPayload = payload;
  provider.identify(identity, traits);
}

/** Solo para tests: devuelve el módulo a su estado inicial. */
export function __resetSupportChatForTests() {
  mounted = 0;
  sinBurbuja = 0;
  scriptInjected = false;
  lastIdentifyPayload = null;
  if (stopObserver) stopObserver();
  stopObserver = null;
  document.documentElement.classList.remove(OPEN_CLASS, NO_LAUNCHER_CLASS);
  document.documentElement.classList.add(HIDDEN_CLASS);
}
